"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

// Per-user state for a conditions detail page (favourite, login, crew
// membership). The page itself is statically rendered and cached, so this
// state is fetched client-side after hydration. Anonymous visitors skip the
// fetch entirely: no auth cookie means no request.

export type SpotUserState = {
  isLoggedIn: boolean;
  isFav: boolean;
  isCrewMember: boolean;
  /** Sports the user gets The Call for at this spot. */
  callSports: string[];
};

const ANON_STATE: SpotUserState = {
  isLoggedIn: false,
  isFav: false,
  isCrewMember: false,
  callSports: [],
};

const SpotUserContext = createContext<SpotUserState>(ANON_STATE);

export function useSpotUser(): SpotUserState {
  return useContext(SpotUserContext);
}

function hasAuthCookie(): boolean {
  return /(^|;\s*)sb-[^=]*-auth-token(\.\d+)?=/.test(document.cookie);
}

export function SpotUserProvider({
  slug,
  crewId,
  children,
}: {
  slug: string;
  crewId?: string | null;
  children: ReactNode;
}) {
  const [state, setState] = useState<SpotUserState>(ANON_STATE);

  useEffect(() => {
    if (!hasAuthCookie()) return;
    let cancelled = false;
    const params = new URLSearchParams({ slug });
    if (crewId) params.set("crewId", crewId);
    fetch(`/api/conditions/user-state?${params.toString()}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data: SpotUserState | null) => {
        if (!cancelled && data) setState({ ...ANON_STATE, ...data });
      })
      .catch(() => {
        // Network failure: keep the anonymous defaults rather than break the page
      });
    return () => {
      cancelled = true;
    };
  }, [slug, crewId]);

  return (
    <SpotUserContext.Provider value={state}>
      {children}
    </SpotUserContext.Provider>
  );
}
